import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  DialogActions,
} from "@mui/material";
import { Category, Program } from "../../store/slices/types";

interface ProgramFormProps {
  program?: Program | null;
  programCategories: Category[];
  onSave: (programData: Partial<Program>) => void;
  onCancel: () => void;
  saving?: boolean;
}

const ProgramForm: React.FC<ProgramFormProps> = ({
  program,
  programCategories,
  onSave,
  onCancel,
  saving = false,
}) => {
  // Helper function to normalize category ID for Select component
  const getNormalizedCategoryId = useCallback(
    (categoryId?: string): string => {
      if (!categoryId) return "";
      const category = programCategories.find(
        (cat) =>
          (cat._id || cat.id) === categoryId ||
          cat._id === categoryId ||
          cat.id === categoryId
      );
      return category
        ? String(category._id || category.id || "")
        : String(categoryId);
    },
    [programCategories]
  );

  // Compute normalized categoryId when program or categories change
  const normalizedCategoryId = useMemo(() => {
    return getNormalizedCategoryId(program?.categoryId);
  }, [program?.categoryId, getNormalizedCategoryId]);

  const [formData, setFormData] = useState({
    title: program?.title || "",
    description: program?.description || "",
    shortDescription: program?.shortDescription || "",
    duration: program?.duration || 0,
    difficulty: program?.difficulty || "beginner",
    categoryId: normalizedCategoryId,
    category: program?.category || "",
    color: program?.color || "#667eea",
    tags: program?.tags?.join(", ") || "",
    estimatedTimePerDay: program?.estimatedTimePerDay || 20,
    isActive: program?.isActive !== undefined ? program.isActive : true,
  });

  // Reset form when program or categories change
  useEffect(() => {
    setFormData({
      title: program?.title || "",
      description: program?.description || "",
      shortDescription: program?.shortDescription || "",
      duration: program?.duration || 0,
      difficulty: program?.difficulty || "beginner",
      categoryId: normalizedCategoryId,
      category: program?.category || "",
      color: program?.color || "#667eea",
      tags: program?.tags?.join(", ") || "",
      estimatedTimePerDay: program?.estimatedTimePerDay || 20,
      isActive: program?.isActive !== undefined ? program.isActive : true,
    });
    setErrors({});
  }, [program, normalizedCategoryId]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.title.trim()) {
      newErrors.title = "Program title is required";
    } else if (formData.title.trim().length < 3) {
      newErrors.title = "Program title must be at least 3 characters";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    }

    if (!formData.shortDescription.trim()) {
      newErrors.shortDescription = "Short description is required";
    } else if (formData.shortDescription.trim().length < 5) {
      newErrors.shortDescription =
        "Short description must be at least 5 characters";
    }

    if (formData.duration <= 0) {
      newErrors.duration = "Duration must be greater than 0";
    } else if (formData.duration > 365) {
      newErrors.duration = "Duration cannot exceed 365 days";
    }

    if (formData.estimatedTimePerDay <= 0) {
      newErrors.estimatedTimePerDay =
        "Estimated time per day must be greater than 0";
    } else if (formData.estimatedTimePerDay > 120) {
      newErrors.estimatedTimePerDay =
        "Estimated time per day cannot exceed 120 minutes";
    }

    if (!formData.categoryId && !formData.category) {
      newErrors.categoryId = "Category is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const programData = {
        ...formData,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag),
        modules: [], // Will be managed separately
      };
      onSave(programData);
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "mindfulness":
        return "🧘‍♀️";
      case "stress-relief":
        return "😌";
      case "sleep":
        return "😴";
      case "anxiety":
        return "😰";
      case "focus":
        return "🎯";
      case "relationships":
        return "💕";
      default:
        return "🧘‍♀️";
    }
  };

  return (
    <Box>
      {/* Basic Information Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📝 Basic Information
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Program Title'
              placeholder='Enter a descriptive title for your program'
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (errors.title) setErrors({ ...errors, title: "" });
              }}
              error={!!errors.title}
              helperText={errors.title}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Short Description'
              placeholder='Brief description for cards and previews'
              value={formData.shortDescription}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  shortDescription: e.target.value,
                });
                if (errors.shortDescription)
                  setErrors({ ...errors, shortDescription: "" });
              }}
              error={!!errors.shortDescription}
              helperText={errors.shortDescription}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label='Full Description'
              placeholder='Provide a detailed description of what users will learn from this program'
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (errors.description)
                  setErrors({ ...errors, description: "" });
              }}
              error={!!errors.description}
              helperText={errors.description}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 140,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Program Configuration Section */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h6'
          fontWeight='600'
          gutterBottom
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          ⚙️ Program Configuration
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Duration (days)'
              placeholder='e.g., 42'
              value={formData.duration || ""}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setFormData({ ...formData, duration: value });
                if (errors.duration) setErrors({ ...errors, duration: "" });
              }}
              error={!!errors.duration}
              helperText={
                errors.duration ||
                "How many days will this program take to complete?"
              }
              inputProps={{
                min: 1,
                max: 365,
                step: 1,
              }}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    📅
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type='number'
              label='Estimated Time Per Day (minutes)'
              placeholder='e.g., 20'
              value={formData.estimatedTimePerDay || ""}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 0;
                setFormData({ ...formData, estimatedTimePerDay: value });
                if (errors.estimatedTimePerDay)
                  setErrors({ ...errors, estimatedTimePerDay: "" });
              }}
              error={!!errors.estimatedTimePerDay}
              helperText={
                errors.estimatedTimePerDay ||
                "How much time per day should users spend?"
              }
              inputProps={{
                min: 1,
                max: 120,
                step: 1,
              }}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    ⏱️
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Difficulty Level</InputLabel>
              <Select
                label='Difficulty Level'
                value={formData.difficulty}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    difficulty: e.target.value as any,
                  })
                }
                sx={{
                  borderRadius: 2,
                  minHeight: 64,
                }}
              >
                <MenuItem value='beginner'>🟢 Beginner</MenuItem>
                <MenuItem value='intermediate'>🟡 Intermediate</MenuItem>
                <MenuItem value='advanced'>🔴 Advanced</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth error={!!errors.categoryId}>
              <InputLabel>Category</InputLabel>
              <Select
                label='Category'
                value={String(formData.categoryId || "")}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    categoryId: e.target.value as string,
                  });
                  if (errors.categoryId)
                    setErrors({ ...errors, categoryId: "" });
                }}
                sx={{
                  borderRadius: 2,
                  minHeight: 64,
                }}
              >
                {programCategories.length === 0 ? (
                  <MenuItem value='' disabled>
                    No categories available. Please create categories first.
                  </MenuItem>
                ) : (
                  programCategories.map((category) => {
                    const categoryId = String(
                      category._id || category.id || ""
                    );
                    return (
                      <MenuItem key={categoryId} value={categoryId}>
                        {category.name}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
              {errors.categoryId && (
                <Typography
                  variant='caption'
                  color='error'
                  sx={{ mt: 0.5, ml: 1.75 }}
                >
                  {errors.categoryId}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Color'
              placeholder='#667eea'
              value={formData.color}
              onChange={(e) =>
                setFormData({ ...formData, color: e.target.value })
              }
              helperText='Hex color code for the program theme'
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    🎨
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label='Tags'
              placeholder='wellness, meditation, stress-relief'
              value={formData.tags}
              onChange={(e) =>
                setFormData({ ...formData, tags: e.target.value })
              }
              helperText='Separate tags with commas to help users find this program'
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 1, color: "text.secondary" }}>
                    🏷️
                  </Typography>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  minHeight: 64,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
        <Button
          onClick={onCancel}
          variant='outlined'
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          disabled={saving}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
            },
          }}
        >
          {saving
            ? "Saving..."
            : program
            ? "Update Program"
            : "Create Program"}
        </Button>
      </Box>
    </Box>
  );
};

export default ProgramForm;

